// ---------------------------------------------------------------------------
// MobileNav, ported from the design system (R10)
//
// <sdr-mobile-nav>: the console's navigation on a phone. A top bar with the
// Menu button, the product line ("SDR Agent by NDI") and one action; Menu
// opens a drawer that holds the SideNav — the reused <sdr-side-nav> — modal
// like a Dialog. A light-DOM Lit element, because the system's MobileNav
// holds two states and an effect:
//
//   - open/defaultOpen/onOpenChange and value/defaultValue/onChange are each
//     the system's useControlled: a given `open` (or `value`, null included)
//     wins and a change is only reported through the callback; otherwise the
//     element keeps its own, starting at `defaultOpen` (as a boolean) or
//     `defaultValue`, read once at the first render like React's useState.
//     Opening or closing reports only a change from the drawn state (the
//     system's setOpen compares with its render's `open`): two presses before
//     the redraw report twice, as there;
//   - the Menu button is the system's Button: ghost, the `list` icon and the
//     word, aria-haspopup="dialog", aria-expanded, and aria-controls naming
//     the drawer while it is open. A click toggles. Enter and Space are the
//     browser's on a native button; the element takes no key of its own there;
//   - the drawer: role="dialog", aria-modal (not inline), its aria-label, the
//     close button first, then the SideNav, given the element's value and a
//     choice handler: a choice reports onChange(id), then closes the drawer
//     (onOpenChange(false)), in that order, as in the system;
//   - whenever the open state changes (by Object.is, like the effect's [open])
//     and the drawer is not inline: first focus on the current page's item,
//     else the first item, else the drawer itself — after the SideNav has
//     drawn (its first render is its own update; updateComplete waits for it).
//     A keydown listener on the document: Escape stops its propagation and
//     closes (not prevented); Tab and Shift+Tab wrap between the first and
//     the last control Tab can reach — the FOCUSABLE selector, tabIndex >= 0
//     (not the roving radios of a SegmentedControl) and with client rects
//     (not hidden controls) — and are prevented only there or when focus is
//     outside the drawer; with no such control every Tab is prevented. The
//     key is read as the native event has it (e.key), as the system's
//     document listener does: not through reactKey();
//   - on close, and when the element leaves the page, the listener goes, and
//     focus goes to the Menu button if it is still in the document (looked up
//     by its id, as in the system). An element put back into the page while
//     open sets its drawer up again, as a remounted React component would;
//   - a mousedown on the backdrop itself closes; one inside the drawer does
//     not. As in the system it is NOT prevented: in Chrome the browser then
//     moves focus to the page after the drawer gave it back to Menu (the S1
//     finding of the Dialog, which the system's Dialog fixed and MobileNav
//     does not; reported for the next version);
//   - no scroll lock: the system sets none;
//   - `inline` (docs and previews): the open drawer sits in its parent,
//     without aria-modal, takes no focus, traps nothing, listens to no key and
//     gives no focus back.
//
// The SideNav inside is <sdr-side-nav> with style="display: contents": the
// element has no box, so the drawer (a flex column) lays out the nav itself,
// as in the system (.sdr-mnav__drawer .sdr-nav fills and scrolls). The spec
// reads through that wrapper when it compares markup.
//
// brandLine() is the system's product line for the top bars (MobileNav,
// AdminNav): the product in plain type, then " by " and the NDI mark, else a
// space and the text endorsement. nav.ts draws the same line inside
// SdrSideNav, privately; it is written here once and admin-nav.ts imports it.
//
// Renames on the element (the system's names would shadow HTMLElement's or
// read as the element's own): className is navClass, label (the SideNav's
// accessible name) is navLabel. Generated ids (the system's useId) are the
// element's own: sdr-mnav-<n>-b for Menu, sdr-mnav-<n>-d for the drawer.
// `actions` and `footer` go through node() (React draws true and false as
// nothing); `actions` shows its box only when truthy, as in the system.
// ---------------------------------------------------------------------------

import { html, LitElement, type PropertyValues, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { button } from './button.js';
import { cx, noBox, node, present } from './helpers.js';
import { type LogoMarkSource, logoMark, type NavItem, type SdrSideNav } from './nav.js';

/** What Tab can reach; the same selector as the system's Dialog and MobileNav (dialog.ts keeps its own copy). */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface BrandLine {
  product?: string;
  endorsement?: string;
  endorsementMark?: LogoMarkSource;
}

/** The product line of SideNav ("SDR Agent by NDI"), for the top bars: the mark wins over the text. */
export function brandLine(p: BrandLine): TemplateResult {
  const endorsement = p.endorsementMark
    ? html`<span class="sdr-nav__endorsement"> by ${logoMark(p.endorsementMark)}</span>`
    : p.endorsement
      ? html`<span class="sdr-nav__endorsement">${` ${p.endorsement}`}</span>`
      : null;
  return html`<span class="sdr-nav__product">${p.product}${endorsement}</span>`;
}

/** Before the first run of the open effect: any open state differs from it. */
const NOT_RUN = Symbol('not run');

let instances = 0;

export class SdrMobileNav extends LitElement {
  static override properties = {
    items: { attribute: false },
    product: { attribute: false },
    endorsement: { attribute: false },
    endorsementMark: { attribute: false },
    workspace: { attribute: false },
    value: { attribute: false },
    defaultValue: { attribute: false },
    onChange: { attribute: false },
    footer: { attribute: false },
    actions: { attribute: false },
    open: { attribute: false },
    defaultOpen: { attribute: false },
    onOpenChange: { attribute: false },
    navLabel: { attribute: false },
    menuLabel: { attribute: false },
    drawerLabel: { attribute: false },
    closeLabel: { attribute: false },
    inline: { attribute: false },
    navClass: { attribute: false },
    ownOpen: { state: true },
    ownValue: { state: true },
  };

  declare items: NavItem[] | undefined;
  /** Set in plain type in the bar and the drawer; the bar shows no brand without it. */
  declare product: string | undefined;
  /** The text fallback, always "by NDI". */
  declare endorsement: string | undefined;
  /** The NDI mark: { src, label: 'NDI', ratio: 2.5677 }. */
  declare endorsementMark: LogoMarkSource | undefined;
  declare workspace: string | undefined;
  declare value: string | undefined;
  declare defaultValue: string | undefined;
  declare onChange: ((id: string) => void) | undefined;
  /** SideNav's footer in the drawer: theme, Sign out. */
  declare footer: unknown;
  /** In the bar, at the right: one icon-only button. */
  declare actions: unknown;
  declare open: boolean | undefined;
  declare defaultOpen: boolean | undefined;
  declare onOpenChange: ((open: boolean) => void) | undefined;
  /** The SideNav's accessible name (the system's label); "Main" by default. */
  declare navLabel: string | undefined;
  /** "Menu" by default. */
  declare menuLabel: string | undefined;
  /** The drawer's accessible name; "Navigation" by default. */
  declare drawerLabel: string | undefined;
  /** The close button's accessible name; "Close navigation" by default. */
  declare closeLabel: string | undefined;
  /** Docs and previews only: the open drawer inside its parent. */
  declare inline: boolean | undefined;
  /** The system's className, on the bar. */
  declare navClass: string | undefined;
  protected declare ownOpen: boolean;
  protected declare ownValue: string | undefined;

  private readonly uid = `sdr-mnav-${++instances}`;
  /** The open state the effect last ran for. */
  private ranFor: unknown = NOT_RUN;
  /** The drawer the listener works on while the effect is in force. */
  private drawer: HTMLElement | null = null;
  /** The first focus, once the SideNav has drawn. */
  private focusing: Promise<unknown> = Promise.resolve();
  /** The open state the last render drew: the system's setOpen compares with it (its render's `open`). */
  private drawnOpen: boolean | undefined;

  override createRenderRoot() {
    return this;
  }

  /** Also waits for the SideNav in the drawer to draw, and for the first focus that follows it. */
  protected override async getUpdateComplete(): Promise<boolean> {
    const done = await super.getUpdateComplete();
    await this.querySelector<SdrSideNav>('sdr-side-nav')?.updateComplete;
    await this.focusing;
    return done;
  }

  /** Open: `open` when given (controlled), else the element's own state. */
  get isOpen(): boolean {
    return this.open !== undefined ? this.open : this.ownOpen;
  }

  /** The current page's id: `value` when given (controlled), else the element's own choice. */
  get current(): string | undefined {
    return this.value !== undefined ? this.value : this.ownValue;
  }

  /** The system's setOpen: only a change from what was drawn is reported (two presses before a redraw report twice). */
  private setOpen(v: boolean) {
    if (v === this.drawnOpen) return;
    if (this.open === undefined) this.ownOpen = v;
    this.onOpenChange?.(v);
  }

  private choose(id: string) {
    if (this.value === undefined) this.ownValue = id;
    this.onChange?.(id);
    this.setOpen(false);
  }

  protected override willUpdate(_changed: PropertyValues<this>) {
    // React's useState(initial): each default is read once, at the first render.
    if (!this.hasUpdated) {
      this.ownOpen = !!this.defaultOpen;
      this.ownValue = this.defaultValue;
    }
    this.drawnOpen = this.isOpen;
  }

  protected override updated(_changed: PropertyValues<this>) {
    // The system's effect on [open]: it runs again only when the open state changes.
    const open = this.isOpen;
    if (Object.is(open, this.ranFor)) return;
    this.ranFor = open;
    this.release();
    const root = this.querySelector<HTMLElement>('.sdr-mnav__drawer');
    if (!open || !root || this.inline) return;
    this.drawer = root;
    document.addEventListener('keydown', this.onKeydown);
    const nav = root.querySelector<SdrSideNav>('sdr-side-nav');
    this.focusing = (nav ? nav.updateComplete : Promise.resolve()).then(() => {
      if (this.drawer !== root) return;
      // First focus: the current page's item, else the first item
      (
        root.querySelector<HTMLElement>('.sdr-nav__item[aria-current="page"]') ||
        root.querySelector<HTMLElement>('.sdr-nav__item') ||
        root
      ).focus();
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
    // Put back into the page: the effect runs again, as for a component that mounts.
    if (this.hasUpdated) this.requestUpdate();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.release();
    this.ranFor = NOT_RUN;
  }

  /** The effect's cleanup: the listener goes, and focus goes back to Menu if it is still in the document. */
  private release() {
    if (!this.drawer) return;
    this.drawer = null;
    document.removeEventListener('keydown', this.onKeydown);
    // Looked up by its id, as in the system. A removed element's Menu is not in the document any more; happy-dom
    // still finds it by id while disconnectedCallback runs, so the element's own isConnected says it.
    const menu = document.getElementById(`${this.uid}-b`);
    if (menu && this.isConnected) menu.focus();
  }

  private readonly onKeydown = (e: KeyboardEvent) => {
    const root = this.drawer;
    if (!root) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.setOpen(false);
      return;
    }
    if (e.key !== 'Tab') return;
    // The ends of the cycle are what Tab can reach: not the roving radios of a SegmentedControl (tabindex -1), not hidden controls
    const f = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      el => el.tabIndex >= 0 && el.getClientRects().length > 0,
    );
    if (!f.length) {
      e.preventDefault();
      return;
    }
    const a = f[0] as HTMLElement;
    const z = f[f.length - 1] as HTMLElement;
    const at = document.activeElement;
    if (e.shiftKey && (at === a || !root.contains(at))) {
      e.preventDefault();
      z.focus();
    } else if (!e.shiftKey && (at === z || !root.contains(at))) {
      e.preventDefault();
      a.focus();
    }
  };

  private onBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) this.setOpen(false);
  }

  override render() {
    const open = this.isOpen;
    const bid = `${this.uid}-b`;
    const did = `${this.uid}-d`;
    return html`<header class=${cx('sdr-mnav', this.navClass)}>
        ${button(
          {
            id: bid,
            variant: 'ghost',
            icon: 'list',
            className: 'sdr-mnav__menu',
            ariaHaspopup: 'dialog',
            ariaExpanded: open,
            ariaControls: open ? did : undefined,
            onClick: () => this.setOpen(!open),
          },
          this.menuLabel || 'Menu',
        )}
        ${this.product ? html`<div class="sdr-mnav__brand">${brandLine(this)}</div>` : null}
        ${present(this.actions) ? html`<div class="sdr-mnav__actions">${node(this.actions)}</div>` : null}
      </header>
      ${
        open
          ? html`<div
              class=${cx('sdr-mnav__scrim', this.inline && 'sdr-mnav__scrim--inline')}
              @mousedown=${(e: MouseEvent) => this.onBackdrop(e)}
            >
              <div
                id=${did}
                role="dialog"
                aria-modal=${ifDefined(this.inline ? undefined : 'true')}
                aria-label=${this.drawerLabel || 'Navigation'}
                tabindex="-1"
                class="sdr-mnav__drawer"
              >
                ${button({
                  variant: 'ghost',
                  size: 'sm',
                  icon: 'close',
                  ariaLabel: this.closeLabel || 'Close navigation',
                  className: 'sdr-mnav__close',
                  onClick: () => this.setOpen(false),
                })}
                <sdr-side-nav
                  style="display: contents"
                  .items=${this.items as NavItem[]}
                  .product=${this.product}
                  .endorsement=${this.endorsement}
                  .endorsementMark=${this.endorsementMark}
                  .workspace=${this.workspace}
                  .footer=${this.footer}
                  .navLabel=${this.navLabel}
                  .value=${this.current}
                  .onChange=${(id: string) => this.choose(id)}
                ></sdr-side-nav>
              </div>
            </div>`
          : null
      }`;
  }
}

if (!customElements.get('sdr-mobile-nav')) customElements.define('sdr-mobile-nav', SdrMobileNav);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-mobile-nav': SdrMobileNav;
  }
}
